/**
 * Directives for module mwApp.subscribers
 */

import moment from 'moment'
import { CREDITCARD_STATUSES } from 'app/core/constants'

/**
 * maxDays directive <br>
 * Check length of subscription
 *
 * @class maxDays
 * @memberof mwApp/subscribers
 * @see mwApp/subscribers
 *
 * @ngdoc directive
 * @name mwApp/subscribers.directive:maxDays
 */
/* @ngInject */
export function maxDays () {
  //

  /**
   * Check, if subscription less, than limit length
   *
   * @param  {Number} days - number of days to limit
   * @param  {moment|Date} [from] - start date
   * @return {Function}
   */
  const lessThan = (days, from) => (s) =>
    moment(from)
      .add(s.length, s.lengthType.toLowerCase())
      .diff(from, 'days') <= days

  return {
    restrict: 'A',
    require: 'ngModel',
    scope: {
      maxLength: '=maxDays',
      lengthType: '=maxDaysType',
      from: '=maxDaysFrom'
    },
    link: function (scope, _element, _attributes, ngModel) {
      var subscriptionLessThan = lessThan(scope.maxLength, scope.from)

      // watch by length type to validate field again
      scope.$watch('lengthType', function (newValue) {
        if (newValue) {
          ngModel.$validate()
        }
      })

      // check subscription max length
      ngModel.$validators.maxDays = function (modelValue) {
        return subscriptionLessThan({
          length: modelValue,
          lengthType: scope.lengthType
        })
      }
    }
  }
}

/**
 * Directive for change status cards.
 *
 * @class cardsStatus
 * @memberof mwApp/subscribers
 * @see mwApp/subscribers
 *
 * @ngdoc directive
 * @name mwApp/subscribers:cardsStatus
 *
 * @example
 * <card-status></card-status>
 */
/* @ngInject */
export function cardStatus ($interval) {
  return {
    restrict: 'E',
    template: /* html */ '<i aria-hidden="true"></i>',
    replace: true,
    link: function (scope, element) {
      const forbiddenStatuses = [
        'PENDING'
      ]

      let interval

      scope.$on('$locationChangeSuccess', function () {
        $interval.cancel(interval)
      })

      scope.$watch('card.status', function () {
        managedStatus()
      }, true)

      /* init sub methods */
      function managedStatus () {
        if (interval) {
          $interval.cancel(interval)
        }

        if (!checkStatus(scope.card.status)) {
          element.removeAttr('class')
          element.addClass('fa fa-spinner fa-spin custom-text-warning')
          element.next('<small>').remove()
          element.after(`&nbsp;<small>${CREDITCARD_STATUSES[scope.card.status]}...</small>`)

          interval = $interval(function () {
            scope.card._update()
              .then((card) => {
                !checkStatus(card.status) || $interval.cancel(interval)
              })
          }, 10000)
        } else {
          setStatus(scope.card.status)
        }
      }

      function setStatus (status) {
        element.removeAttr('class')
        element.next('<small>').remove()

        switch (status) {
          case 'OK':
            element.after(`<small class="text-success">${CREDITCARD_STATUSES[status]}</small>`)
            break
          case 'PENDING':
            element.after(`<small class="custom-text-warning">${CREDITCARD_STATUSES[status]}</small>`)
            break
          case 'INVALID':
            element.after(`<small class="text-warning">${CREDITCARD_STATUSES[status]}</small>`)
            break
          default:
            element.after('<small class="text-info">Undefined</small>')
            break
        }
      }

      function checkStatus (status) {
        return forbiddenStatuses.indexOf(status) === -1
      }
    }
  }
}

/**
 * Directive for bind data to ui-view.
 *
 * @class bindScopeToView
 * @memberof mwApp/subscribers
 * @see mwApp/subscribers
 *
 * @ngdoc directive
 * @name mwApp/subscribers:bindScopeToView
 *
 * @example
 * <div bind-scope-to-view view-data="data" view-name="viewname"></div>
 */
/* @ngInject */
export function bindScopeToView ($compile) {
  return {
    restrict: 'A',
    scope: {
      viewData: '<',
      viewName: '@'
    },
    link: function (scope, element) {
      const viewName = scope.viewName || ''
      // create isolated scope
      const isolatedScope = scope.$new(true)
      isolatedScope.data = scope.viewData

      const viewElement = angular.element(`<div ui-view="${viewName}"></div>`)
      viewElement.data('$scope', isolatedScope)
      element.append(viewElement)
      $compile(viewElement)(isolatedScope)
    }
  }
}

/**
 * Directive for format content name
 *
 * @class contentName
 * @memberof mwApp/subscribers
 * @see mwApp/subscribers
 *
 * @ngdoc directive
 * @name mwApp/subscribers:contentName
 *
 * @example
 * <td content-name item="item" share-scope="{...}"></td>
 */
/* @ngInject */
export function contentName () {
  return {
    restrict: 'A',
    scope: {
      item: '=',
      shareScope: '<'
    },
    template: `
      <div class="library-content__name">
      <a ng-if="allowRedirectToContent" ui-sref="{{item.href}}">{{item.contentName}}</a>
      <a ng-if="!allowRedirectToContent">{{item.contentName}}</a>
      <span class="" ng-if="item.contentType === 'EPISODE'">
        {{item.tvShowName && item.tvShowName !== item.season ? item.tvShowName : ''}}
        {{item.seasonName && item.tvShowName ? '/' : ''}}
        {{item.seasonName && item.seasonName !== item.episode ? item.seasonName : ''}}
      </span>
      <span class="" ng-if="item.contentType === 'SEASON' && item.tvShowName !== item.season">{{item.tvShowName}}</span>
      </div>
    `,
    controller: ['$scope', function ($scope) {
      function calculateHref (item) {
        switch (item.contentType) {
          case 'CHANNEL':
            return `app.channels.channel.edit({ channelId: ${item.contentId} })`
          case 'VOD':
            return `app.vods.vod.edit({ vodId: ${item.contentId} })`
          case 'LIVE_EVENT':
            return `app.liveEvents.liveEvent.edit({ leId: ${item.contentId} })`
          case 'TV_SHOW':
            return `app.shows.show.edit({ showId: ${item.contentId} })`
          case 'EPISODE':
            return `app.shows.show.episodes.episode.edit({ episodeId: ${item.contentId}, showId: ${item.tvShowId}, seasonId: ${item.seasonId || '"none"'} })`
          case 'SEASON':
            return `app.shows.show.edit({ showId: ${item.tvShowId} })`
          case 'CHANNEL_SET':
            return `app.organizations.organization.contentSets.channel({ channelSetId: ${item.contentId}, packageId: ${item.networkId} })`
          case 'VOD_SET':
            return `app.organizations.organization.contentSets.vod({ vodSetId: ${item.contentId}, packageId: ${item.networkId} })`
          case 'TV_SHOW_SET':
            return `app.organizations.organization.contentSets.show({ showSetId: ${item.contentId}, packageId: ${item.networkId} })`
          default:
            return ''
        }
      }

      if ($scope.item.contentType.includes('CHANNEL')) {
        $scope.allowRedirectToContent = $scope.$root.hasAccess('viewChannel')
      } else if ($scope.item.contentType.includes('LIVE')) {
        $scope.allowRedirectToContent = $scope.$root.hasAccess('viewLiveEvent')
      } else if ($scope.item.contentType.includes('VOD')) {
        $scope.allowRedirectToContent = $scope.$root.hasAccess('viewVod')
      } else {
        $scope.allowRedirectToContent = $scope.$root.hasAccess('viewVodSeries')
      }
      $scope.item.href = calculateHref($scope.item)
    }]
  }
}

/**
 * Directive for column library name placeholder
 *
 * @class contentNameSearchPlaceholder
 * @memberof mwApp/subscribers
 * @see mwApp/subscribers
 *
 * @ngdoc directive
 * @name mwApp/subscribers:contentNameSearchPlaceholder
 *
 * @example
 * <input content-name-search-placeholder  placeholder="Search..."/>
 */
/* @ngInject */
export function contentNameSearchPlaceholder () {
  return {
    restrict: 'A',
    link: function (scope, element) {
      if (scope.vm.tabIndex) {
        let placeholderValue = 'Search by partial or full match'
        switch (scope.vm.LIBRARY_CONTENT_TYPE_MAP[scope.vm.tabIndex]) {
          case 'SEASON':
            placeholderValue = 'Search on Season or TV Show names'
            break
          case 'EPISODE':
            placeholderValue = 'Search on Episode, Season or TV Show names'
            break
        }
        element.attr('placeholder', placeholderValue)
      }
    }
  }
}
//
/* @ngInject */
// export function uiSrefNoHash ($state) {
//   return {
//     restrict: 'A',
//     link: function (scope, element, attrs) {
//       element.on('click', function (event) {
//         event.preventDefault()
//         // window.location.hash = ''
//         $state.go(attrs.uiSrefNoHash, scope.$eval(attrs.srefParams))
//       })
//     }
//   }
// }
